// Dokumenten-Assistent (seit 21.10.2026): reine Logik fuer die Seite unter
// Vertraege -> Bearbeitung -> "Dokumenten-Assistent" und fuer die Testkarte der
// Automatik "dokumenten-automatik" in der Admin-Uebersicht.
//
// Ersetzt das eigene Tool "Bitrix Doku Assistant" (upload.en-power.eu). Der
// Server (api/automation/dokumenten-assistent, Handler unter api/_lib/) erkennt
// mit der Bitrix-KI, welche Dokumente auf welchen Seiten stehen, schneidet sie
// zu und legt sie in den Deal-Feldern ab. Welches Feld, bestimmt NUR der Server -
// der Browser schickt nie eine Feldkennung.
//
// Hier steht nichts, was das Netz oder React braucht: Teilen in Upload-Teile,
// Deal-ID, Statustexte und -farben, Kennzahlen, Zusammenfassungen. Getestet in
// dokumentenAssistent.test.js.

import { T } from './designTokens';

// --- Deal-ID ----------------------------------------------------------------

/**
 * "D_123", "d123", " 123 " oder ein Bitrix-Link (".../crm/deal/details/123/")
 * werden zu "123". Alles andere wird "" - der Server prueft genauso streng.
 */
export function normalisiereDealId(eingabe) {
  const text = String(eingabe ?? '').trim();
  const ausLink = text.match(/crm\/deal\/details\/(\d{1,12})(?:\/|$|\?)/i);
  const kandidat = ausLink ? ausLink[1] : text.replace(/^D_?/i, '');
  if (!/^\d{1,12}$/.test(kandidat)) return '';
  return Number(kandidat) > 0 ? kandidat : '';
}

// --- Vertragsarten ----------------------------------------------------------

export const VERTRAGSARTEN = [
  { wert: 'PV', label: 'PV' },
  { wert: 'BESS', label: 'BESS' },
  { wert: 'KLEINSPEICHER', label: 'Kleinspeicher' },
  { wert: 'DIGITAL', label: 'Digitaler Vertrag', hinweis: 'nur Anlagen – der Vertrag liegt schon digital vor' },
];

export function vertragsartLabel(wert) {
  return VERTRAGSARTEN.find((art) => art.wert === wert)?.label || String(wert || '–');
}

/**
 * Welche Vertragsart nach "Vertrag aus dem Deal holen" gilt. Der Server meldet
 * nur PV oder BESS (das gefuellte Vertragsfeld). Eine bewusste Wahl, die dazu
 * passt, bleibt stehen: Kleinspeicher liegt im BESS-Feld, und "Digitaler
 * Vertrag" heisst gerade, dass der Vertrag schon im Deal liegt.
 */
export function vertragsartNachDealFund(aktuell, gefunden) {
  if (aktuell === 'DIGITAL') return aktuell;
  if (aktuell === 'KLEINSPEICHER' && gefunden === 'BESS') return aktuell;
  return gefunden === 'PV' || gefunden === 'BESS' ? gefunden : aktuell;
}

// --- Datei ------------------------------------------------------------------

/** Hoechstgroesse der Eingabe, wie am Server (80 MB). */
export const MAX_DATEI_BYTES = 80 * 1024 * 1024;

export const DATEI_ANNAHME = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const TYP_NACH_ENDUNG = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' };
const ERLAUBTE_TYPEN = new Set(Object.values(TYP_NACH_ENDUNG));

/** MIME-Typ der Datei; fehlt er (manche Systeme liefern ""), aus der Endung. */
export function mimeTypFuer(datei) {
  const typ = String(datei?.type || '').toLowerCase();
  if (ERLAUBTE_TYPEN.has(typ)) return typ;
  const endung = String(datei?.name || '').split('.').pop().toLowerCase();
  return TYP_NACH_ENDUNG[endung] || typ;
}

/** "" wenn die Datei angenommen wird, sonst der Grund. */
export function pruefeDatei(datei) {
  if (!datei) return 'Bitte eine Datei wählen.';
  if (!ERLAUBTE_TYPEN.has(mimeTypFuer(datei))) {
    return `„${datei.name}“ ist weder PDF noch JPG noch PNG.`;
  }
  if (!datei.size) return `„${datei.name}“ ist leer.`;
  if (datei.size > MAX_DATEI_BYTES) {
    return `„${datei.name}“ ist ${groesseText(datei.size)} groß – erlaubt sind höchstens ${groesseText(MAX_DATEI_BYTES)}.`;
  }
  return '';
}

// --- Upload in Teilen -------------------------------------------------------
//
// Vercel nimmt hoechstens 4,5 MB je Anfrage an. Jeder Teil traegt deshalb
// hoechstens 3 MB Base64 (wie der Exposé-Teil-Upload). Die Rohteile sind durch 3
// teilbar: Dann endet nur der letzte Teil mit "="-Auffuellung, und die Teile
// hintereinander ergeben genau das Base64 der ganzen Datei - egal, ob der Server
// die Texte aneinanderhaengt oder jeden Teil einzeln entschluesselt.

export const TEIL_BASE64_ZEICHEN = 3 * 1024 * 1024;
export const TEIL_ROH_BYTES = (TEIL_BASE64_ZEICHEN / 4) * 3;

/** Bytebereiche der Teile: [{ index, start, ende }], ende exklusiv. */
export function teilBereiche(groesse, teilBytes = TEIL_ROH_BYTES) {
  const gesamt = Math.max(0, Math.floor(Number(groesse) || 0));
  const bereiche = [];
  for (let start = 0; start < gesamt; start += teilBytes) {
    bereiche.push({ index: bereiche.length, start, ende: Math.min(gesamt, start + teilBytes) });
  }
  return bereiche;
}

export function bytesZuBase64(bytes) {
  const BLOCK = 0x8000; // in Bloecken, sonst sprengt fromCharCode den Stack
  let binaer = '';
  for (let i = 0; i < bytes.length; i += BLOCK) {
    binaer += String.fromCharCode.apply(null, bytes.subarray(i, i + BLOCK));
  }
  return btoa(binaer);
}

/** Kennungen prueft der Server mit genau diesem Muster. */
export const KENNUNG_MUSTER = /^[A-Za-z0-9_-]{8,64}$/;

export function neueUploadId() {
  const roh = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `u${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `da${roh}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

/** Liest einen Blob als Bytes. FileReader statt blob.arrayBuffer(): geht auch in aelteren Browsern. */
export function leseAlsBytes(blob) {
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve(new Uint8Array(leser.result));
    leser.onerror = () => reject(leser.error || new Error('Die Datei ließ sich nicht lesen.'));
    leser.readAsArrayBuffer(blob);
  });
}

/** Netzfehler, 429 und 5xx lohnen einen zweiten Versuch; 4xx nicht. */
export function istWiederholbarerFehler(fehler) {
  const status = fehler?.response?.status;
  if (!status) return Boolean(fehler?.isAxiosError || fehler?.request || fehler?.code === 'ECONNABORTED');
  return status === 429 || status >= 500;
}

const warteMs = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

async function mitWiederholung(aufruf, { versuche, warte, pauseMs }) {
  const hoechstens = Math.max(1, versuche);
  for (let versuch = 1; versuch < hoechstens; versuch += 1) {
    try {
      return await aufruf();
    } catch (fehler) {
      if (!istWiederholbarerFehler(fehler)) throw fehler;
      await warte(pauseMs);
    }
  }
  return aufruf();
}

/**
 * Laedt die Datei Teil fuer Teil hoch (op "teil") und meldet den Fortschritt.
 * Jeder Teil wird bei einem voruebergehenden Fehler einmal wiederholt - ein
 * gleicher Index ueberschreibt am Server nur denselben Teil. Liefert die Zahl
 * der Teile (fuer op "starten").
 */
export async function ladeDateiInTeilenHoch({
  datei, uploadId, sendeTeil, beiFortschritt = () => {}, leseBytes = leseAlsBytes,
  warte = warteMs, versuche = 2, pauseMs = 1500,
}) {
  const bereiche = teilBereiche(datei?.size);
  for (const bereich of bereiche) {
    const data = bytesZuBase64(await leseBytes(datei.slice(bereich.start, bereich.ende)));
    const koerper = { op: 'teil', uploadId, index: bereich.index, data };
    await mitWiederholung(() => sendeTeil(koerper), { versuche, warte, pauseMs });
    beiFortschritt(bereich.index + 1, bereiche.length);
  }
  return bereiche.length;
}

/** "" wenn das Formular abgeschickt werden kann, sonst was fehlt. */
export function formularFehler({ dealId, vertragsart, quelle, datei }) {
  if (!dealId) return 'Bitte eine gültige Deal-ID eingeben – nur Ziffern, z. B. 12345 oder D_12345.';
  if (!VERTRAGSARTEN.some((art) => art.wert === vertragsart)) return 'Bitte die Vertragsart wählen.';
  if (quelle === 'deal') return '';
  return datei ? pruefeDatei(datei) : 'Bitte eine Datei wählen oder den Vertrag aus dem Deal holen.';
}

/** Koerper fuer op "starten" - genau die Felder, die der Server erwartet. */
export function startKoerper({ dealId, vertragsart, quelle, uploadId, teile, datei, nurAnalyse }) {
  const basis = { op: 'starten', dealId, vertragsart, quelle, nurAnalyse: Boolean(nurAnalyse) };
  if (quelle !== 'upload') return basis;
  return { ...basis, uploadId, teile, dateiname: datei?.name || '', mimeType: mimeTypFuer(datei) };
}

/** Meldung fuer die Oberflaeche: zuerst `detail` vom Server, nie still. */
export function fehlerText(fehler, ersatz = 'Die Aktion ist fehlgeschlagen.') {
  const daten = fehler?.response?.data;
  const detail = typeof daten?.detail === 'string' ? daten.detail.trim() : '';
  if (detail) return detail;
  const status = fehler?.response?.status;
  if (status === 413) return 'Die Anfrage war für den Server zu groß.';
  if (status === 401) return 'Die Anmeldung ist abgelaufen – bitte neu anmelden.';
  if (fehler?.code === 'ECONNABORTED') return `${ersatz} Der Server hat nicht rechtzeitig geantwortet.`;
  if (fehler && !fehler.response && fehler.request) return `${ersatz} Keine Verbindung zum Server.`;
  if (status) return `${ersatz} (HTTP ${status})`;
  return fehler?.message ? `${ersatz} (${fehler.message})` : ersatz;
}

// --- Status -----------------------------------------------------------------

export const TOENE = {
  gut: { farbe: T.gut, grund: T.gutGrund, rand: T.gutRand },
  info: { farbe: T.info, grund: T.infoGrund, rand: T.infoRand },
  warn: { farbe: T.warn, grund: T.warnGrund, rand: T.warnRand },
  gefahr: { farbe: T.gefahr, grund: T.gefahrGrund, rand: T.gefahrRand },
  leise: { farbe: T.muted, grund: T.grund, rand: T.rand },
};

const STATUS = {
  auftrag: {
    wartet: ['Wartet', 'leise'],
    laeuft: ['Läuft', 'info'],
    // Kein Status des Servers, sondern seine Angabe `haengend` (offen und seit Minuten
    // unveraendert) - siehe anzeigeStatus.
    haengend: ['Hängt', 'warn'],
    fertig: ['Fertig', 'gut'],
    teilweise: ['Teilweise', 'warn'],
    pruefen: ['Von Hand prüfen', 'warn'],
    fehler: ['Fehler', 'gefahr'],
    liegengelassen: ['Liegengelassen', 'leise'],
  },
  dokument: {
    wartet: ['Wartet', 'leise'],
    hochgeladen: ['Hochgeladen', 'gut'],
    // Vertrag aus dem Deal, der schon in seinem Zielfeld liegt - bleibt, wie er ist.
    uebersprungen: ['Unverändert', 'leise'],
    fehler: ['Fehler', 'gefahr'],
    probelauf: ['Probelauf', 'info'],
  },
  nachweis: {
    abgelegt: ['Abgelegt', 'gut'],
    probelauf: ['Probelauf', 'info'],
    nicht_erstellt: ['Nicht erstellt', 'warn'],
    fehler: ['Fehler', 'gefahr'],
  },
};

/** Text und Farben einer Statusmarke. art: 'auftrag' | 'dokument' | 'nachweis'. */
export function statusAnzeige(status, art = 'auftrag') {
  const [text, ton] = STATUS[art]?.[status] || [status ? String(status) : 'Unbekannt', 'leise'];
  return { text, ton, ...TOENE[ton] };
}

const OFFEN = new Set(['wartet', 'laeuft']);

export const ABFRAGE_MS = 4000;

export function istOffen(auftrag) {
  return OFFEN.has(auftrag?.status);
}

/**
 * Offen, aber seit Minuten unveraendert (Funktion abgestorben, Staffel abgerissen). Das sagt
 * der Server (`haengend`, gleiche Regel wie dort istHaengend) - die Uhr des Browsers zaehlt nicht.
 */
export function istHaengend(auftrag) {
  return istOffen(auftrag) && auftrag?.haengend === true;
}

/** Status fuer die Marke: ein haengender Auftrag zeigt "Hängt" statt "Läuft". */
export function anzeigeStatus(auftrag) {
  return istHaengend(auftrag) ? 'haengend' : auftrag?.status;
}

/**
 * Die Liste wird nur abgefragt, solange etwas wartet oder laeuft - haengende Auftraege zaehlen
 * nicht: Bei ihnen tut sich von allein nichts, alle 4 s nachzufragen bringt nichts.
 */
export function brauchtAbfrage(auftraege) {
  return Array.isArray(auftraege) && auftraege.some((a) => istOffen(a) && !istHaengend(a));
}

export function kannWiederholen(auftrag) {
  return auftrag?.status === 'fehler' || auftrag?.status === 'pruefen' || istHaengend(auftrag);
}

export function kannEntfernen(auftrag) {
  return Boolean(auftrag?.status) && (!istOffen(auftrag) || istHaengend(auftrag));
}

/**
 * Kennzahlen wie im alten Tool; "fehler" zaehlt alles, was jemand ansehen muss - auch
 * haengende Auftraege, die dafuer nicht mehr in Warteschlange/Aktiv stehen.
 */
export function kennzahlen(auftraege) {
  const liste = Array.isArray(auftraege) ? auftraege : [];
  const laufend = liste.filter((a) => !istHaengend(a));
  const zaehle = (...status) => laufend.filter((a) => status.includes(a?.status)).length;
  const haengend = liste.filter(istHaengend).length;
  return {
    warteschlange: zaehle('wartet'),
    aktiv: zaehle('laeuft'),
    abgeschlossen: zaehle('fertig', 'liegengelassen'),
    fehler: zaehle('fehler', 'teilweise', 'pruefen') + haengend,
    pruefen: zaehle('pruefen'),
    haengend,
  };
}

/** Unterzeile der Kennzahl "Fehler": was davon von Hand zu pruefen ist oder haengt. */
export function fehlerZeile(zahlen) {
  const teile = [];
  if (zahlen?.pruefen) teile.push(`${zahlen.pruefen} von Hand prüfen`);
  if (zahlen?.haengend) teile.push(`${zahlen.haengend} ${zahlen.haengend === 1 ? 'hängt' : 'hängen'}`);
  return teile.length ? `davon ${teile.join(', ')}` : 'oder von Hand prüfen';
}

/** Prozent der gelesenen Seiten oder null, solange die Seitenzahl unbekannt ist. */
export function fortschrittAnteil(auftrag) {
  const gesamt = Number(auftrag?.fortschritt?.seitenGesamt) || 0;
  if (!gesamt) return null;
  const gelesen = Math.max(0, Math.min(gesamt, Number(auftrag.fortschritt.seitenGelesen) || 0));
  return Math.round((gelesen / gesamt) * 100);
}

// --- Texte ------------------------------------------------------------------

/** [1, 2, 3, 5, 7, 8] -> "1–3, 5, 7–8". */
export function seitenText(seiten) {
  const zahlen = Array.from(new Set((Array.isArray(seiten) ? seiten : []).map(Number)))
    .filter((n) => Number.isInteger(n) && n > 0)
    .sort((a, b) => a - b);
  const laeufe = [];
  zahlen.forEach((n) => {
    const letzter = laeufe[laeufe.length - 1];
    if (letzter && n === letzter[1] + 1) letzter[1] = n;
    else laeufe.push([n, n]);
  });
  return laeufe.map(([von, bis]) => (von === bis ? String(von) : `${von}–${bis}`)).join(', ');
}

/** Die Seiten des Nachweises: "Seiten 1 und 8". */
export function nachweisSeitenText(seiten) {
  const liste = Array.isArray(seiten) ? seiten.filter((n) => Number(n) > 0) : [];
  if (liste.length === 0) return '';
  if (liste.length === 1) return `Seite ${liste[0]}`;
  if (liste.length === 2) return `Seiten ${liste[0]} und ${liste[1]}`;
  return `Seiten ${seitenText(liste)}`;
}

export function groesseText(bytes) {
  const zahl = Number(bytes) || 0;
  if (zahl < 1024 * 1024) return `${Math.max(1, Math.round(zahl / 1024)).toLocaleString('de-DE')} KB`;
  return `${(zahl / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

const SEITEN_ARTEN = {
  vertrag: 'Vertrag',
  vollmacht_grundbuch: 'Vollmacht Grundbuch',
  vollmacht_netzanfrage: 'Vollmacht Netzanfrage',
  vollmacht_bauunterlagen: 'Vollmacht für Bauunterlagen',
  widerrufsbelehrung: 'Widerrufsbelehrung',
  sonstiges: 'Sonstiges',
};

/** Was die KI auf einer Seite erkannt hat; ohne Urteil "unbeurteilt". */
export function seitenArtText(art) {
  return SEITEN_ARTEN[art] || 'unbeurteilt';
}

export function jaNein(wert) {
  if (wert === true || wert === 'true') return 'ja';
  if (wert === false || wert === 'false') return 'nein';
  return '–';
}

/** Die Zeile "Ernsthaftigkeitsnachweis" in ganzen Worten. */
export function nachweisText(nachweis) {
  if (!nachweis) return 'Noch nicht erstellt.';
  const seiten = nachweisSeitenText(nachweis.seiten);
  const grund = String(nachweis.grund || '').trim();
  switch (nachweis.status) {
    case 'abgelegt':
      return `Abgelegt – ${seiten || 'Seiten unbekannt'}${nachweis.dateiname ? ` (${nachweis.dateiname})` : ''}.${nachweis.unsicher ? ' Unterschriftsseite nicht sicher erkannt – bitte prüfen.' : ''}`;
    case 'probelauf':
      return `Würde abgelegt – ${seiten || 'Seiten unbekannt'}.${nachweis.unsicher ? ' Unterschriftsseite nicht sicher erkannt – bitte prüfen.' : ''}`;
    case 'nicht_erstellt':
      return `Nicht erstellt – ${grund || 'ohne Angabe'}.`;
    case 'fehler':
      return `Fehler – ${grund || 'ohne Angabe'}.`;
    default:
      return grund || 'Unbekannter Stand.';
  }
}

function nachweisKurz(nachweis) {
  switch (nachweis?.status) {
    case 'abgelegt': return nachweis.unsicher ? 'Nachweis abgelegt (prüfen)' : 'Nachweis abgelegt';
    case 'probelauf': return 'Nachweis würde abgelegt';
    case 'nicht_erstellt': return 'kein Nachweis';
    case 'fehler': return 'Nachweis fehlgeschlagen';
    default: return '';
  }
}

function anzahl(zahl, einzahl, mehrzahl) {
  return `${zahl} ${zahl === 1 ? einzahl : mehrzahl}`;
}

/** "seit 12 Minuten", "seit 3 Stunden", ab einem Tag "seit 20.10., 14:05". */
export function seitText(wert, jetzt = Date.now()) {
  const zeit = Date.parse(wert || '');
  if (!Number.isFinite(zeit)) return '';
  const minuten = Math.max(1, Math.round((jetzt - zeit) / 60000));
  if (minuten < 60) return `seit ${anzahl(minuten, 'Minute', 'Minuten')}`;
  const stunden = Math.round(minuten / 60);
  if (stunden < 24) return `seit ${anzahl(stunden, 'Stunde', 'Stunden')}`;
  return `seit ${zeitText(wert)}`;
}

/** Was abgelegt wurde; ein unveraendert gelassener Vertrag zaehlt nicht als "nicht abgelegt". */
function ablageTeile(dokumente) {
  const zaehle = (status) => dokumente.filter((d) => d.status === status).length;
  const unveraendert = zaehle('uebersprungen');
  const zuAblegen = dokumente.length - unveraendert;
  const teile = [];
  if (!dokumente.length) teile.push('Keine Dokumente zum Ablegen');
  if (zuAblegen) teile.push(`${zaehle('hochgeladen')} von ${anzahl(zuAblegen, 'Dokument', 'Dokumenten')} abgelegt`);
  if (unveraendert) teile.push(`${unveraendert} unverändert im Deal`);
  if (zaehle('fehler')) teile.push(`${zaehle('fehler')} fehlgeschlagen`);
  return teile;
}

/** Eine Zeile, was aus dem Auftrag geworden ist. */
export function zusammenfassung(auftrag, jetzt = Date.now()) {
  if (!auftrag) return '';
  const grund = String(auftrag.fehler || '').trim();
  if (istHaengend(auftrag)) {
    const seit = seitText(auftrag.aktualisiertAm || auftrag.erstelltAm, jetzt);
    return `Hängt${seit ? ` ${seit}` : ''} – erneut versuchen oder entfernen.`;
  }
  switch (auftrag.status) {
    case 'wartet': return 'Wartet auf den Start.';
    case 'laeuft': return auftrag.schritt || 'Wird bearbeitet …';
    case 'pruefen': return `Von Hand prüfen${grund ? `: ${grund}` : ''} – es wurde nichts hochgeladen.`;
    case 'fehler': return `Fehler: ${grund || 'ohne Angabe'}`;
    case 'liegengelassen': return `Liegengelassen${grund ? `: ${grund}` : '.'}`;
    default: break;
  }
  const dokumente = Array.isArray(auftrag.dokumente) ? auftrag.dokumente : [];
  const teile = auftrag.nurAnalyse
    ? [`Probelauf: ${anzahl(dokumente.length, 'Dokument', 'Dokumente')} erkannt`]
    : ablageTeile(dokumente);
  const nachweis = nachweisKurz(auftrag.nachweis);
  if (nachweis) teile.push(nachweis);
  return teile.join(' · ');
}

/**
 * Die Phase nach der Automatik ("NAP starten"). Bei einem echten Lauf steht sie
 * im Auftrag; beim Probelauf wird nichts verschoben - dann sagt die Zeile, was
 * passiert waere. null: nichts zu sagen (noch offen).
 *
 * Probelauf: Der Server sagt in phase.wuerdeVerschieben, ob die Automatik den
 * Deal verschoben haette (false etwa, wenn er gar nicht in "Vertrag Digital
 * vorhanden" steht - Grund in phase.grund). Nur bei true heisst es "würde
 * verschieben"; "fertig" allein genuegt dafuer nicht (Review 21.10.2026).
 */
export function phaseAussage(auftrag) {
  if (!auftrag || istOffen(auftrag)) return null;
  const phase = auftrag.phase || null;
  const grund = String(phase?.grund || auftrag.fehler || '').trim();
  if (!auftrag.nurAnalyse) {
    if (phase?.status === 'verschoben') return { ton: 'gut', text: 'Nach „NAP starten“ verschoben.' };
    if (phase?.status === 'nicht_verschoben') {
      return { ton: 'warn', text: `Nicht nach „NAP starten“ verschoben${grund ? ` – ${grund}` : ''}.` };
    }
    return null;
  }
  if (phase?.wuerdeVerschieben === false) {
    return { ton: 'warn', text: String(phase.grund || '').trim() || 'Würde nicht nach „NAP starten“ verschieben.' };
  }
  if (phase?.wuerdeVerschieben === true) return { ton: 'gut', text: 'Würde nach „NAP starten“ verschieben.' };
  if (auftrag.status === 'fertig') return null;
  const wegen = grund || statusAnzeige(auftrag.status).text;
  return { ton: 'warn', text: `Würde nicht nach „NAP starten“ verschieben – ${wegen}.` };
}

export function zeitText(wert) {
  if (!wert) return '';
  const datum = new Date(wert);
  if (Number.isNaN(datum.getTime())) return '';
  return datum.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// --- Liste ------------------------------------------------------------------

/** Ersetzt den Auftrag mit derselben id oder stellt ihn nach vorn - ohne die Liste zu veraendern. */
export function mitAuftrag(liste, auftrag) {
  const alt = Array.isArray(liste) ? liste : [];
  if (!auftrag?.id) return alt;
  return alt.some((a) => a.id === auftrag.id)
    ? alt.map((a) => (a.id === auftrag.id ? auftrag : a))
    : [auftrag, ...alt];
}

/**
 * Fuehrt die Liste vom Server mit dem lokalen Stand zusammen. Eine Abfrage, die
 * vor dem Anlegen losging, kennt den neuen Auftrag noch nicht - er bliebe sonst
 * verschwunden, und mit ihm die Abfrage. Ebenso taucht ein gerade entfernter
 * Auftrag nicht aus einer alten Antwort wieder auf.
 * frisch/entfernt: Mengen von Auftrags-ids.
 */
export function fuehreListenZusammen(server, lokal, { frisch = new Set(), entfernt = new Set() } = {}) {
  const vomServer = (Array.isArray(server) ? server : []).filter((a) => a?.id && !entfernt.has(a.id));
  const bekannt = new Set(vomServer.map((a) => a.id));
  const nochNichtDa = (Array.isArray(lokal) ? lokal : []).filter((a) => frisch.has(a?.id) && !bekannt.has(a.id));
  return [...nochNichtDa, ...vomServer];
}
